import { v4 as uuid } from 'uuid';
import type { SceneStratum } from '../media/sketch/scene';
import type { StratumRow } from './stratumRows';

/**
 * Where the Schichten of a description meet the Schichten drawn on its
 * sketches: the sketch SEEDS the list, and the measures are what is left to
 * fill in.
 *
 * WHAT TIES A ROW TO A MARK IS THE MARK'S ID, not its name. Matching by name
 * broke on every rename there is — the row, the mark, the sketch the row is
 * named after, or merely switching the UI language — and the mark was then
 * seeded a second time.
 *
 * The seeding stays one-directional and conservative: it adds a row, takes back
 * one it added that is still empty, and keeps a row's name in step with its mark
 * ONLY while nobody has edited that name by hand. It never touches a measure and
 * never removes a row someone has measured.
 */

/** The separator between a sketch's name and its layer's, in a seeded name. */
const SEPARATOR = ' - ';

export interface SketchStrata {
    /** What the sketch is called — its caption, else "Skizze 3". */
    sketchLabel: string;
    /** The Schicht marks on it, in the order they were placed. */
    strata: SceneStratum[];
}

/**
 * The row name a Schicht on a sketch gets: "Ostprofil - Schicht A", or
 * "Skizze 1 - Schicht A" where the sketch has no caption. `stratumWord` is the
 * TRANSLATED word for a layer, so the name reads in the recorder's language.
 */
export function seedStratumName(
    sketchLabel: string,
    stratumWord: string,
    label: string
): string {
    return `${sketchLabel}${SEPARATOR}${stratumWord} ${label}`.trim();
}

/** Whether a row holds any measurement — what makes it worth keeping. */
function isMeasured(row: StratumRow): boolean {
    return (
        row.length.trim() !== '' ||
        row.width.trim() !== '' ||
        row.depth.trim() !== '' ||
        (row.diameter ?? '').trim() !== ''
    );
}

/**
 * Whether this row came from a sketch — only such a row is ever taken back. A
 * row typed by hand is the recorder's, and so is one seeded before the
 * bookkeeping existed: those carry neither mark, and "I cannot tell" means
 * "leave it".
 */
export function isSeededRow(row: StratumRow): boolean {
    return row.sourceId !== undefined || row.seededName !== undefined;
}

interface WantedStratum {
    /** Absent where the sketch was saved before marks kept their ids. */
    sourceId?: string;
    name: string;
}

/** What the sketches currently ask the list for, in the order they were drawn. */
function wantedStrata(
    sketches: readonly SketchStrata[],
    stratumWord: string
): WantedStratum[] {
    const wanted: WantedStratum[] = [];
    const seen = new Set<string>();
    for (const sketch of sketches) {
        for (const mark of sketch.strata) {
            const label = mark.label.trim();
            if (label === '') {
                continue;
            }
            const name = seedStratumName(
                sketch.sketchLabel,
                stratumWord,
                label
            );
            // The same layer is often boxed twice — left and right of a pit in
            // a Profil, say. That is one Schicht, so it is one row.
            if (seen.has(name)) {
                continue;
            }
            seen.add(name);
            wanted.push({ ...(mark.id ? { sourceId: mark.id } : {}), name });
        }
    }
    return wanted;
}

/** The name the seeding last wrote into a row (its own name, before v16). */
function watermark(row: StratumRow): string {
    return (row.seededName ?? row.name).trim();
}

/** Bring a claimed row in line with its mark; returns it AS IS if it already is. */
function applyMark(row: StratumRow, want: WantedStratum): StratumRow {
    // The name follows the drawing only while it still reads exactly as the
    // seeding wrote it; past that someone renamed it on purpose. The tie to the
    // mark is the id, and it holds regardless.
    const untouched = watermark(row) === row.name.trim();
    const name = untouched ? want.name : row.name;
    const seededName = untouched ? want.name : watermark(row);
    const sourceId = want.sourceId ?? row.sourceId;
    if (
        name === row.name &&
        seededName === row.seededName &&
        sourceId === row.sourceId
    ) {
        return row;
    }
    return {
        ...row,
        name,
        seededName,
        ...(sourceId ? { sourceId } : {})
    };
}

/**
 * Reconcile the hand-kept list with what the sketches currently show. Every
 * mark claims its row — by id, or, for a sketch or row that predates ids, by
 * the name the seeding wrote (which is how old rows adopt their mark once and
 * for all). A mark with no row gets one; a seeded row whose mark is gone is
 * dropped, unless measured.
 *
 * Returns the SAME array when nothing changed: the caller re-runs this whenever
 * the sketches change, and an identical list must not mark the form dirty.
 */
export function mergeSketchStrata(
    rows: readonly StratumRow[],
    sketches: readonly SketchStrata[],
    stratumWord: string
): StratumRow[] {
    const wanted = wantedStrata(sketches, stratumWord);
    const claims = new Map<StratumRow, WantedStratum>();
    const claimed = new Set<WantedStratum>();

    /** Let every unclaimed mark take the first row that answers to `matches`. */
    function claimRows(
        matches: (row: StratumRow, want: WantedStratum) => boolean
    ) {
        for (const want of wanted) {
            if (claimed.has(want)) {
                continue;
            }
            const row = rows.find(
                (candidate) =>
                    !claims.has(candidate) && matches(candidate, want)
            );
            if (row) {
                claims.set(row, want);
                claimed.add(want);
            }
        }
    }

    // The id first: it is the tie that renaming cannot cut.
    claimRows(
        (row, want) =>
            want.sourceId !== undefined && row.sourceId === want.sourceId
    );
    // Then the name, for the sketches saved and the rows seeded before ids
    // existed. This runs once per row: the claim stamps the id on it.
    claimRows((row, want) => watermark(row) === want.name);

    let changed = false;
    const kept: StratumRow[] = [];
    for (const row of rows) {
        const want = claims.get(row);
        if (!want) {
            // Nothing on any sketch stands for this row any more: an empty
            // seeded row goes with its mark, everything else stays.
            if (isSeededRow(row) && !isMeasured(row)) {
                changed = true;
                continue;
            }
            kept.push(row);
            continue;
        }
        const next = applyMark(row, want);
        changed = changed || next !== row;
        kept.push(next);
    }

    const added = wanted
        .filter((want) => !claimed.has(want))
        .map((want) => ({
            id: uuid(),
            name: want.name,
            seededName: want.name,
            ...(want.sourceId ? { sourceId: want.sourceId } : {}),
            length: '',
            width: '',
            depth: ''
        }));

    if (!changed && added.length === 0) {
        return rows as StratumRow[];
    }
    return [...kept, ...added];
}
